import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { stateSchema } from '~/stores/app';
import api from '~/utils/api';
import { CORSO_URL } from '~/utils/constants';
import { logError } from '~/utils/observability';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { storeParamSchema } from '~/utils/schemas';

const settingsSchema = z.object(
  {
    settings: stateSchema.shape.settings.unwrap(),
  },
  {
    errorMap: () => ({
      message: 'Invalid settings data. Please try again later.',
    }),
  },
);

export const {
  loader: crewOrderLookupLoader,
  useLoaderData: useCrewOrderLookupData,
} = makeLoader(({ params }, appStore) =>
  Promise.all([
    settingsSchema.parseAsync(appStore.getState()),
    storeParamSchema.parseAsync(params), // ensure store param defined and valid
  ])
    .then(([{ settings }]) => {
      const {
        isActive,
        storeUrl = CORSO_URL,
        registrations: {
          isEnabled: isRegistrationEnabled,
          isStartWarrantyClaimEnabled,
        },
        theme: {
          orderLookupDetailText,
          orderLookupRegistrationText,
          orderNumberDescriptionText,
        },
      } = settings;

      if (!isActive) {
        return redirect(`inactive?redirect=${encodeURI(storeUrl)}`);
      }

      const offerRegistration =
        isRegistrationEnabled && isStartWarrantyClaimEnabled;

      const orderLookupLabel =
        offerRegistration ? `Order or Registration Number` : `Order Number`;

      const startRegistrationText =
        offerRegistration ?
          (orderLookupRegistrationText ??
          `Didn't purchase from our online store?`)
        : undefined;

      return {
        orderNumberDescription: orderNumberDescriptionText,
        orderLookupDetailText,
        orderLookupLabel,
        startRegistrationText,
      };
    })
    .catch((error) => {
      logError(
        new Error('Invalid store parameters or settings', { cause: error }),
      );

      return redirect('/');
    }),
);

export const FormKeys = {
  EmailOrPostalCode: 'emailOrPostalCode',
  OrderNo: 'orderNo',
} as const;

const formSchema = zfd.formData(
  z.object(
    {
      [FormKeys.OrderNo]: zfd.text(z.string().min(1)),
      [FormKeys.EmailOrPostalCode]: zfd.text(z.string().min(1)),
    },
    {
      errorMap: () => ({
        message: 'Missing required fields. Check form and try again.',
      }),
    },
  ),
);

const handleActionError = (error: unknown) => ({
  ok: false,
  message: error instanceof Error ? error.message : String(error),
});

export const {
  action: crewOrderLookupAction,
  useActionResult: useCrewOrderLookupActionResult,
} = makeAction(({ formData }, appStore) =>
  Promise.all([
    settingsSchema.parseAsync(appStore.getState()),
    formSchema.parseAsync(formData),
  ])
    .then(([{ settings }, query]) =>
      api.fetchCrewOrder({
        params: { storefrontId: settings.storefrontId },
        query,
      }),
    )
    .then((order) => {
      appStore.dispatch(appStore.actions.setOrder(order));

      return redirect(`order/${order.idFromPlatform}`);
    })
    .catch(handleActionError),
);

export const {
  action: orderLookupAction,
  useActionResult: useOrderLookupActionResult,
} = makeAction(({ formData }, appStore) =>
  formSchema
    .parseAsync(formData)
    .then((query) => api.fetchProtectionOrder({ query }))
    .then((order) => {
      appStore.dispatch(appStore.actions.setOrder(order));

      return redirect(`./reorder/${order.idFromPlatform}`);
    })
    .catch(handleActionError),
);
